import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';
import { getApiUrl } from '../lib/runtimeConfig';

/**
 * Customer-facing OIDC client. The bundle ships with NO baked-in
 * issuer/client_id/audience — those come from the backend's
 * GET /api/v1/auth/config endpoint at app boot. The API URL itself
 * is also runtime — nginx substitutes __API_URL__ in index.html from
 * the pod env at container start. Same image works across staging/prod
 * and survives Zitadel re-bootstraps without a rebuild.
 */

interface AuthConfigResponse {
  customer: {
    issuer: string;
    clientId: string;
    audience: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
  };
  // admin block exists in the response but isn't used here.
}

let _userManager: UserManager | null = null;
let _initPromise: Promise<UserManager> | null = null;

/**
 * Resolves the singleton UserManager, fetching `/api/v1/auth/config`
 * the first time and caching the instance for the rest of the session.
 *
 * Throws if the backend isn't reachable or the issuer/clientId come
 * back empty — calling `signinRedirect()` with an empty authority
 * would silently fail in oidc-client-ts, and a hard error here lets
 * AuthProvider show a clear message instead.
 */
export function getOidcUserManager(): Promise<UserManager> {
  if (_userManager) return Promise.resolve(_userManager);
  if (!_initPromise) {
    _initPromise = initialize().catch((err) => {
      // Reset so a retry can re-attempt rather than re-throwing the
      // cached failure forever.
      _initPromise = null;
      throw err;
    });
  }
  return _initPromise;
}

async function initialize(): Promise<UserManager> {
  const url = `${getApiUrl()}/api/v1/auth/config`;
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`[Auth] /api/v1/auth/config returned ${res.status}`);
  }
  const config = (await res.json()) as AuthConfigResponse;
  const customer = config.customer;
  if (!customer.issuer || !customer.clientId) {
    throw new Error(
      '[Auth] /api/v1/auth/config returned an empty issuer or clientId — ' +
      'is the streamko-zitadel-credentials Secret populated yet?',
    );
  }

  // Zitadel-specific scopes:
  //   openid + profile + email — standard OIDC.
  //   offline_access — refresh-token issuance for silent renew.
  //   urn:zitadel:iam:org:project:id:<project-id>:aud — adds the streamko-customer
  //     project ID to the access token's `aud` claim so the backend's
  //     ValidAudience=Auth:Customer:Audience check (which is the project ID,
  //     not the client_id) passes.
  //   urn:zitadel:iam:org:projects:roles — emits the urn:zitadel:iam:org:project:roles
  //     claim that ZitadelClaimsTransformer expects on the API side.
  const scopeParts = [
    'openid',
    'profile',
    'email',
    'offline_access',
    'urn:zitadel:iam:org:projects:roles',
  ];
  if (customer.audience) {
    scopeParts.splice(scopeParts.length - 1, 0, `urn:zitadel:iam:org:project:id:${customer.audience}:aud`);
  }

  const settings: UserManagerSettings = {
    authority: customer.issuer,
    client_id: customer.clientId,
    redirect_uri: customer.redirectUri || `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: customer.postLogoutRedirectUri || `${window.location.origin}/signup`,
    response_type: 'code',
    scope: scopeParts.join(' '),
    loadUserInfo: true,
    automaticSilentRenew: true,
    // oidc-client-ts v3 uses the refresh_token automatically (when offline_access
    // is granted) — no silent_redirect_uri / hidden iframe required.
    userStore: new WebStorageStateStore({ store: window.localStorage, prefix: 'streamko-oidc:' }),
  };

  _userManager = new UserManager(settings);
  return _userManager;
}
